// @wc-ignore-file

/**
 * Lazy-loading Mermaid renderer with Kairo-wide safety limits (CR-21).
 *
 * The mermaid library (~2MB) is only imported when a diagram actually needs
 * rendering, and is initialized once with security-safe defaults: strict
 * sanitization for untrusted workspace-authored content, plus size and time
 * bounds so a single diagram can never degrade the surrounding surface.
 *
 * Keep this module free of React/DOM specifics so every rich-text surface can
 * share it.
 */

type MermaidApi = typeof import("mermaid")["default"];

/** Language identifier of Markdown code fences treated as Mermaid diagrams. */
export const MERMAID_LANGUAGE = "mermaid";

/** Hard cap on diagram source length, mirrored into mermaid's maxTextSize. */
export const MAX_SOURCE_LENGTH = 30_000;

/** Upper bound on edge count, guarding against pathological graph diagrams. */
export const MAX_EDGES = 500;

/** A single diagram gets this long to parse + render before we give up. */
export const RENDER_TIMEOUT_MS = 5_000;

export type MermaidFailureReason = "invalid" | "too-large" | "timeout" | "runtime";

export class MermaidRenderError extends Error {
  readonly reason: MermaidFailureReason;

  constructor(reason: MermaidFailureReason, message: string) {
    super(message);
    this.name = "MermaidRenderError";
    this.reason = reason;
  }
}

let mermaidPromise: Promise<MermaidApi> | null = null;

function loadMermaid(): Promise<MermaidApi> {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "neutral",
        fontFamily: "inherit",
        maxTextSize: MAX_SOURCE_LENGTH,
        maxEdges: MAX_EDGES,
        // Errors surface through our own UI; never inject mermaid's error DOM.
        suppressErrorRendering: true,
      });
      return mermaid;
    });
    // Allow a retry on a failed chunk load instead of caching the rejection.
    mermaidPromise.catch(() => {
      mermaidPromise = null;
    });
  }
  return mermaidPromise;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

let renderSeq = 0;

/**
 * Renders Mermaid source to an SVG string.
 *
 * Rejects with {@link MermaidRenderError} when the source is empty, too large,
 * invalid, too slow to render, or when the mermaid runtime itself fails.
 */
export async function renderMermaid(source: string): Promise<string> {
  const code = source.trim();
  if (!code) {
    throw new MermaidRenderError("invalid", "Diagram source is empty");
  }
  if (code.length > MAX_SOURCE_LENGTH) {
    throw new MermaidRenderError(
      "too-large",
      `Diagram source exceeds ${MAX_SOURCE_LENGTH} characters`,
    );
  }

  const render = (async () => {
    let mermaid: MermaidApi;
    try {
      mermaid = await loadMermaid();
    } catch (error) {
      throw new MermaidRenderError("runtime", errorMessage(error));
    }

    try {
      await mermaid.parse(code);
    } catch (error) {
      throw new MermaidRenderError("invalid", errorMessage(error));
    }

    try {
      const { svg } = await mermaid.render(`kairo-mermaid-${++renderSeq}`, code);
      return svg;
    } catch (error) {
      throw new MermaidRenderError("runtime", errorMessage(error));
    }
  })();

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(
        new MermaidRenderError("timeout", `Rendering took longer than ${RENDER_TIMEOUT_MS}ms`),
      );
    }, RENDER_TIMEOUT_MS);
  });

  try {
    return await Promise.race([render, timeout]);
  } finally {
    clearTimeout(timer);
    // A late rejection after losing the race must not surface as unhandled.
    render.catch(() => {});
  }
}
